/* eslint-disable unicorn/filename-case */
import {
  closestCenter,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  pointerWithin,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Appointment01Icon, CheckListIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import type { ActionItemGroup } from '@pique/core/modules/properties/services/action-items-service';
import {
  isFocusScheduleOverdue,
  NO_DUE_DATE_KEY,
} from '@pique/core/modules/properties/services/action-items-service';
import {
  ACTION_ITEM_STATUS,
  type ActionItemStatus,
  type ActionItemGroupBy,
  type SerializedTodoItem,
} from '@pique/core/modules/properties/services/schema';
import { isActionItemCompleted } from '@pique/core/modules/properties/services/utils';
import { posthog } from '@pique/core/posthog';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  FOCUS_ZONE_ID,
  FocusDropZone,
} from '../../pages/workspace/detail-page/tabs/pique-action-items-list/ActionItemsCalendar';


import { TodoItemComponent } from '../../pages/workspace/detail-page/tabs/pique-action-items-list/TodoItemComponent';
import { ActionItemGroupComponent } from './action-item-group';
import * as groupStyles from './action-item-group.css';
import {
  getSortableItemVisibility,
  shouldHideSourceItemForFocusPreview,
} from './sortable-action-items-panel-utils';

const CALENDAR_DROP_PREFIX = 'calendar-slot:';

export interface GroupedReorderEvent {
  itemId: string;
  groupBy: ActionItemGroupBy;
  fromGroupId: string;
  toGroupId: string;
  fromIndex: number;
  toIndex: number;
  nextGroups: ActionItemGroup[];
}

export interface SortableActionItemsPanelGrouping {
  groupBy: ActionItemGroupBy;
  groups: ActionItemGroup[];
  onReorder: (event: GroupedReorderEvent) => void;
  onGroupTitleClick?: (group: ActionItemGroup) => void;
  allowCrossGroupDrag?: boolean;
}

export interface ActionItemSelectionState {
  selectable: boolean;
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
}

interface ExternalPanelDropTarget {
  groupId?: string;
  beforeItemId?: string;
}

export interface SortableActionItemsPanelProps {
  items: SerializedTodoItem[];
  onReorder: (oldIndex: number, newIndex: number) => void;
  onItemComplete?: (itemId: string, pageId: string) => void;
  className?: string;
  style?: React.CSSProperties;
  enableDragHandle?: boolean;
  containerStyle?: React.CSSProperties;
  itemClassName?: string;
  initialNewIds?: Set<string>;
  grouping?: SortableActionItemsPanelGrouping;
  focusedItems?: SerializedTodoItem[];
  onSetFocusedItems?: (items: SerializedTodoItem[]) => void;
  selection?: ActionItemSelectionState;
  headerComponent?: React.ReactNode;
  hideFocusedItems?: boolean;
  hideDragHint?: boolean;
}

interface SortableItemProps {
  item: SerializedTodoItem;
  onItemComplete?: (itemId: string, pageId: string) => void;
  enableDragHandle?: boolean;
  itemClassName?: string;
  isNew?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelectedChange?: () => void;
  dimmed?: boolean;
  dragHidden?: boolean;
  dataAttributes?: Record<string, string | undefined>;
}

const SortableItem = ({
  item,
  onItemComplete,
  enableDragHandle,
  itemClassName,
  isNew,
  selectable,
  selected,
  onSelectedChange,
  dimmed,
  dragHidden,
  dataAttributes,
}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    setActivatorNodeRef,
  } = useSortable({ id: item.id });

  const visibility = getSortableItemVisibility({
    isDragging,
    dragHidden: Boolean(dragHidden),
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: visibility.opacity,
    visibility: visibility.visibility,
    position: 'relative' as const,
    pointerEvents: visibility.pointerEvents,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={itemClassName}
      data-dragging={isDragging || undefined}
      {...dataAttributes}
    >
      <div
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        style={{
          width: '100%',
          height: '100%',
          touchAction: 'none',
        }}
      >
        <TodoItemComponent
          item={item}
          isDraggable={enableDragHandle}
          isNew={isNew}
          compact={true}
          onComplete={onItemComplete}
          selectable={selectable}
          selected={selected}
          onSelectedChange={onSelectedChange}
          dimmed={dimmed}
        />
      </div>
    </div>
  );
};

const groupDropId = (groupId: string) => `group:${groupId}`;

const GroupDropZone = ({
  groupId,
  className,
  style,
  children,
}: {
  groupId: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) => {
  const { setNodeRef } = useDroppable({ id: groupDropId(groupId) });
  return (
    <div ref={setNodeRef} className={className} style={style}>
      {children}
    </div>
  );
};

const FocusAreaDropZone = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { setNodeRef } = useDroppable({ id: FOCUS_ZONE_ID });

  return (
    <div ref={setNodeRef} style={{ display: 'flex', flexDirection: 'column' }}>
      {children}
    </div>
  );
};

export const SortableActionItemsPanel = ({
  items,
  onReorder,
  onItemComplete,
  className,
  style,
  enableDragHandle = true,
  containerStyle,
  itemClassName,
  initialNewIds,
  grouping,
  focusedItems = [],
  onSetFocusedItems,
  selection,
  headerComponent,
  hideFocusedItems = true,
}: SortableActionItemsPanelProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(
    () => new Set([NO_DUE_DATE_KEY])
  );
  const [calendarDropActiveId, setCalendarDropActiveId] = useState<
    string | null
  >(null);
  const [calendarHoverActiveId, setCalendarHoverActiveId] = useState<
    string | null
  >(null);
  const [focusAreaDropActive, setFocusAreaDropActive] = useState(false);
  const [externalPanelDropTarget, setExternalPanelDropTarget] =
    useState<ExternalPanelDropTarget | null>(null);
  const [dndAutoScrollEnabled, setDndAutoScrollEnabled] = useState(true);

  // Local state to track focused items during a drag operation for smooth visual feedback
  const [dragFocusedItems, setDragFocusedItems] = useState<
    SerializedTodoItem[] | null
  >(null);

  // Track pre-drag focused items for comparison on drag end
  const preDragFocusedIdsRef = useRef<Set<string>>(new Set());
  const preDragFocusedOrderRef = useRef<string[]>([]);

  const currentFocusedItems = dragFocusedItems ?? focusedItems;
  const knownItemsById = useMemo(() => {
    const map = new Map<string, SerializedTodoItem>();
    const addItems = (nextItems: SerializedTodoItem[]) => {
      nextItems.forEach(item => {
        if (!map.has(item.id)) {
          map.set(item.id, item);
        }
      });
    };

    addItems(items);
    addItems(focusedItems);

    if (grouping) {
      grouping.groups.forEach(group => addItems(group.items));
    }

    return map;
  }, [focusedItems, grouping, items]);
  const previewFocusedIds = useMemo(
    () => new Set(currentFocusedItems.map(item => item.id)),
    [currentFocusedItems]
  );
  const committedFocusedIds = useMemo(
    () => new Set(focusedItems.map(item => item.id)),
    [focusedItems]
  );
  const shouldHideSourceItemForPendingFocusCommit = useCallback(
    (itemId: string) =>
      shouldHideSourceItemForFocusPreview({
        itemId,
        previewFocusedIds,
        committedFocusedIds,
      }),
    [committedFocusedIds, previewFocusedIds]
  );
  const hiddenFocusedIds = useMemo(() => {
    if (!dragFocusedItems || activeId) {
      return committedFocusedIds;
    }
    return previewFocusedIds;
  }, [activeId, committedFocusedIds, dragFocusedItems, previewFocusedIds]);
  const getKnownDragItem = useCallback(
    (itemId: string) => {
      const item = knownItemsById.get(itemId);
      if (!item) return null;

      const wasFocused = committedFocusedIds.has(itemId);
      const isFocusedInPreview = previewFocusedIds.has(itemId);

      if (wasFocused && !isFocusedInPreview) {
        return {
          ...item,
          isFocused: false,
          status: ACTION_ITEM_STATUS.TODO as ActionItemStatus,
        };
      }

      return item;
    },
    [committedFocusedIds, knownItemsById, previewFocusedIds]
  );

  useEffect(() => {
    if (!dragFocusedItems) return;
    if (activeId) return;
    const dragIds = dragFocusedItems.map(item => item.id);
    const focusedIds = focusedItems.map(item => item.id);
    const isApplied =
      dragIds.length === focusedIds.length &&
      dragIds.every((id, index) => focusedIds[index] === id);
    if (isApplied) {
      setDragFocusedItems(null);
    }
  }, [activeId, dragFocusedItems, focusedItems]);

  const selectedIdsSet = useMemo(
    () => new Set(selection?.selectedIds ?? []),
    [selection?.selectedIds]
  );

  const handleSelectAll = useCallback(
    (groupItems: SerializedTodoItem[]) => {
      if (!selection) return;
      const groupIds = groupItems.map(i => i.id);
      const allSelected = groupIds.every(id => selectedIdsSet.has(id));
      const otherIds = selection.selectedIds.filter(
        id => !groupIds.includes(id)
      );
      if (allSelected) {
        selection.onSelectedIdsChange(otherIds);
      } else {
        selection.onSelectedIdsChange([...otherIds, ...groupIds]);
      }
    },
    [selection, selectedIdsSet]
  );

  const handleItemSelectChange = useCallback(
    (itemId: string) => {
      if (!selection) return;
      const isSelected = selectedIdsSet.has(itemId);
      if (isSelected) {
        selection.onSelectedIdsChange(
          selection.selectedIds.filter(id => id !== itemId)
        );
      } else {
        selection.onSelectedIdsChange([...selection.selectedIds, itemId]);
      }
    },
    [selection, selectedIdsSet]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const collisionDetection = useCallback<CollisionDetection>(args => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return closestCenter(args);
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    document.body.style.cursor = 'grabbing';
    setFocusAreaDropActive(false);
    setExternalPanelDropTarget(null);
    setActiveId(active.id as string);
    setCalendarDropActiveId(null);
    setCalendarHoverActiveId(null);
    setDragFocusedItems(focusedItems);
    preDragFocusedIdsRef.current = new Set(focusedItems.map(i => i.id));
    preDragFocusedOrderRef.current = focusedItems.map(i => i.id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    const activeIdStr = String(active.id);
    const overIdStr = over ? String(over.id) : '';
    const isOverCalendarSlot = overIdStr.startsWith(CALENDAR_DROP_PREFIX);
    const isOverFocusZone = overIdStr === FOCUS_ZONE_ID;
    const isOverCalendarSurface = isOverCalendarSlot || isOverFocusZone;
    setFocusAreaDropActive(isOverCalendarSurface);
    if (isOverCalendarSlot) {
      setCalendarDropActiveId(activeIdStr);
    } else if (calendarDropActiveId) {
      setCalendarDropActiveId(null);
    }
    if (isOverCalendarSurface) {
      setCalendarHoverActiveId(activeIdStr);
    } else if (calendarHoverActiveId) {
      setCalendarHoverActiveId(null);
    }

    if (isOverCalendarSurface) {
      if (dndAutoScrollEnabled) setDndAutoScrollEnabled(false);
    } else {
      if (!dndAutoScrollEnabled) setDndAutoScrollEnabled(true);
    }

    const activeStartedInFocus = preDragFocusedIdsRef.current.has(activeIdStr);
    const activeRect =
      active.rect.current.translated ?? active.rect.current.initial;
    const pointerY =
      activeRect !== null
        ? activeRect.top + activeRect.height / 2
        : null;
    if (activeStartedInFocus && over && !isOverCalendarSurface) {
      if (grouping) {
        let groupId: string | undefined;
        let beforeItemId: string | undefined;

        if (overIdStr.startsWith('group:')) {
          groupId = overIdStr.slice('group:'.length);
        } else {
          for (const group of grouping.groups) {
            const overIndex = group.items.findIndex(item => item.id === overIdStr);
            if (overIndex !== -1) {
              groupId = group.id;
              const overMidY = over.rect.top + over.rect.height / 2;
              const insertAfterOver = pointerY !== null && pointerY > overMidY;
              beforeItemId = insertAfterOver
                ? group.items[overIndex + 1]?.id
                : group.items[overIndex]?.id;
              break;
            }
          }
        }

        setExternalPanelDropTarget(groupId ? { groupId, beforeItemId } : null);
      } else {
        setExternalPanelDropTarget(
          (() => {
            const overIndex = items.findIndex(item => item.id === overIdStr);
            if (overIndex === -1) return null;
            const overMidY = over.rect.top + over.rect.height / 2;
            const insertAfterOver = pointerY !== null && pointerY > overMidY;
            return {
              beforeItemId: insertAfterOver
                ? items[overIndex + 1]?.id
                : items[overIndex]?.id,
            };
          })()
        );
      }
    } else {
      setExternalPanelDropTarget(null);
    }

    if (!onSetFocusedItems || !over) return;

    if (activeIdStr === overIdStr) return;

    // Determine if we are interacting with the focus zone
    const isOverFocusedItem = currentFocusedItems.some(i => i.id === overIdStr);
    const isDroppingInFocusArea =
      isOverFocusZone || isOverFocusedItem || isOverCalendarSlot;

    const isCurrentlyFocused = currentFocusedItems.some(
      i => i.id === activeIdStr
    );

    // Scenario 1: Dragging INTO Focus Zone
    if (isDroppingInFocusArea && !isCurrentlyFocused) {
      const itemToAdd = getKnownDragItem(activeIdStr);
      if (itemToAdd) {
        setDragFocusedItems(prev => {
          const current = prev || [];
          if (current.some(i => i.id === activeIdStr)) return current;

          const newItems = [...current];
          if (isOverFocusedItem) {
            const overIndex = newItems.findIndex(i => i.id === overIdStr);
            newItems.splice(overIndex, 0, itemToAdd);
          } else {
            newItems.push(itemToAdd);
          }
          return newItems;
        });
      }
      return;
    }

    // Scenario 2: Reordering WITHIN Focus Zone
    if (isCurrentlyFocused && isDroppingInFocusArea) {
      setDragFocusedItems(prev => {
        const current = prev || [];
        const oldIndex = current.findIndex(i => i.id === activeIdStr);
        const newIndex = current.findIndex(i => i.id === overIdStr);

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          return arrayMove(current, oldIndex, newIndex);
        }
        return current;
      });
      return;
    }

    // Scenario 3: Dragging OUT of Focus Zone
    if (isCurrentlyFocused && !isDroppingInFocusArea) {
      setDragFocusedItems(prev => {
        const current = prev || [];
        return current.filter(i => i.id !== activeIdStr);
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const overId = over?.id as string | undefined;
    const overIdStr = String(over?.id ?? '');

    // Commit the focus state changes first (UI priority), then track analytics async
    if (onSetFocusedItems && dragFocusedItems) {
      const preDragIds = preDragFocusedIdsRef.current;
      const preDragOrder = preDragFocusedOrderRef.current;
      const finalFocusedItems = dragFocusedItems;

      const finalOrder = finalFocusedItems.map(i => i.id);
      const didFocusChange =
        preDragOrder.length !== finalOrder.length ||
        preDragOrder.some((id, idx) => id !== finalOrder[idx]);

      if (didFocusChange) {
        onSetFocusedItems(finalFocusedItems);

        // Defer analytics to avoid blocking main thread after drag
        queueMicrotask(() => {
          const postDragIds = new Set(finalFocusedItems.map(i => i.id));

          // Track items added to focus zone
          const addedItems = finalFocusedItems.filter(
            i => !preDragIds.has(i.id)
          );
          for (const item of addedItems) {
            posthog.capture('action_item_updated', {
              item_id: item.id,
              item_title:
                typeof item.title === 'string'
                  ? item.title
                  : String(item.title || ''),
              new_focus_count: finalFocusedItems.length,
            });
          }

          // Track items removed from focus zone
          const removedItems = focusedItems.filter(i => !postDragIds.has(i.id));
          for (const item of removedItems) {
            posthog.capture('action_item_updated', {
              item_id: item.id,
              item_title:
                typeof item.title === 'string'
                  ? item.title
                  : String(item.title || ''),
              remaining_focus_count: finalFocusedItems.length,
            });
          }
        });
      }
    }

    // Reset local drag state
    document.body.style.cursor = '';
    preDragFocusedIdsRef.current = new Set();
    preDragFocusedOrderRef.current = [];
    setActiveId(null);
    setCalendarDropActiveId(null);
    setCalendarHoverActiveId(null);
    setDndAutoScrollEnabled(true);
    setFocusAreaDropActive(false);
    setExternalPanelDropTarget(null);

    if (overIdStr.startsWith(CALENDAR_DROP_PREFIX)) {
      return;
    }
    const isOverFocusZone = overIdStr === FOCUS_ZONE_ID;
    const isOverFocusedItem = currentFocusedItems.some(i => i.id === overIdStr);

    if (
      !isOverFocusZone &&
      !isOverFocusedItem
    ) {
      if (grouping) {
        const activeItemId = active.id as string;
        if (!overId) {
          return;
        }
        const groups = grouping.groups;
        const locateItem = (itemId: string) => {
          for (const group of groups) {
            const index = group.items.findIndex(it => it.id === itemId);
            if (index !== -1) return { groupId: group.id, index };
          }
          return null;
        };
        const from = locateItem(activeItemId);
        const toGroupId = overId.startsWith('group:')
          ? overId.slice('group:'.length)
          : locateItem(overId)?.groupId;
        if (!toGroupId) {
          return;
        }
        const toGroup = groups.find(g => g.id === toGroupId);
        if (!toGroup) {
          return;
        }

        // Item dragged from the focus/in-progress list (not in any group)
        if (!from) {
          const activeItem = getKnownDragItem(activeItemId);
          if (!activeItem) return;
          const nextGroups = groups.map(g => ({ ...g, items: [...g.items] }));
          const nextToGroup = nextGroups.find(g => g.id === toGroupId)!;
          const placeholderTarget =
            externalPanelDropTarget?.groupId === toGroupId
              ? externalPanelDropTarget
              : null;
          const rawInsertIndex = placeholderTarget?.beforeItemId
            ? nextToGroup.items.findIndex(
                item => item.id === placeholderTarget.beforeItemId
              )
            : nextToGroup.items.length;
          const insertIndex =
            rawInsertIndex >= 0 ? rawInsertIndex : nextToGroup.items.length;
          nextToGroup.items.splice(insertIndex, 0, activeItem);
          grouping.onReorder({
            itemId: activeItemId,
            groupBy: grouping.groupBy,
            fromGroupId: '__focus__',
            toGroupId,
            fromIndex: 0,
            toIndex: insertIndex,
            nextGroups,
          });
          return;
        }

        const fromGroup = groups.find(g => g.id === from.groupId);
        if (!fromGroup) {
          return;
        }
        if (
          grouping.allowCrossGroupDrag === false &&
          toGroupId !== from.groupId
        ) {
          return;
        }
        const activeItem = fromGroup.items[from.index];
        if (!activeItem) {
          return;
        }
        const nextGroups = groups.map(g => ({ ...g, items: [...g.items] }));
        const nextFromGroup = nextGroups.find(g => g.id === from.groupId)!;
        const nextToGroup = nextGroups.find(g => g.id === toGroupId)!;
        nextFromGroup.items.splice(from.index, 1);
        const rawToIndex = overId.startsWith('group:')
          ? nextToGroup.items.length
          : nextToGroup.items.findIndex(it => it.id === overId);
        const toIndex =
          rawToIndex === -1 ? nextToGroup.items.length : rawToIndex;
        const adjustedToIndex =
          from.groupId === toGroupId && toIndex > from.index
            ? toIndex - 1
            : toIndex;
        nextToGroup.items.splice(adjustedToIndex, 0, activeItem);
        grouping.onReorder({
          itemId: activeItemId,
          groupBy: grouping.groupBy,
          fromGroupId: from.groupId,
          toGroupId,
          fromIndex: from.index,
          toIndex: adjustedToIndex,
          nextGroups,
        });
        return;
      }

      if (active.id !== over?.id) {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over?.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          onReorder(oldIndex, newIndex);
          posthog.capture('action_items_reordered', {
            from_index: oldIndex,
            to_index: newIndex,
          });
        }
      }
    }
  };

  const activeItem = activeId ? knownItemsById.get(activeId) ?? null : null;

  const handleDragCancel = useCallback(() => {
    document.body.style.cursor = '';
    preDragFocusedIdsRef.current = new Set();
    preDragFocusedOrderRef.current = [];
    setActiveId(null);
    setCalendarDropActiveId(null);
    setCalendarHoverActiveId(null);
    setDndAutoScrollEnabled(true);
    setFocusAreaDropActive(false);
    setExternalPanelDropTarget(null);
    setDragFocusedItems(null);
  }, []);

  const renderExternalDropIndicator = useCallback(
    (key: string) => {
      if (!activeItem) return null;
      return (
        <div key={key} style={{ opacity: 0.4, pointerEvents: 'none' }} aria-hidden>
          <TodoItemComponent item={activeItem} compact onComplete={() => {}} />
        </div>
      );
    },
    [activeItem]
  );

  const handleItemComplete = useCallback(
    (itemId: string, pageId: string) => {
      onItemComplete?.(itemId, pageId);
    },
    [onItemComplete]
  );

  if (grouping) {
    const isBacklogGrouping = grouping.groupBy === 'dueDate';

    // Keep SortableContext stable during drag. The focus-zone preview can use
    // `currentFocusedItems`, but the source sortable lists should only react to
    // committed focus state from props.
    const displayGroups = grouping.groups.map(g => ({
      ...g,
      items: hideFocusedItems
        ? g.items.filter(item => !hiddenFocusedIds.has(item.id))
        : g.items,
    }));

    const groupScheduleStats = new Map(
      grouping.groups.map(group => {
        const incompleteItems = group.items.filter(
          item => !isActionItemCompleted(item)
        );
        const totalIncomplete = incompleteItems.length;
        const scheduledCount = incompleteItems.filter(
          item => Boolean(item.scheduledAt) && !isFocusScheduleOverdue(item)
        ).length;
        const scheduledPercent =
          totalIncomplete > 0
            ? Math.round((scheduledCount / totalIncomplete) * 100)
            : 0;
        return [
          group.id,
          {
            scheduledCount,
            totalIncomplete,
            scheduledPercent,
          },
        ] as const;
      })
    );

    let primaryItemAssigned = false;
    const renderGroupItems = (
      groupItems: SerializedTodoItem[],
      groupIndex: number
    ) => (
      <SortableContext
        items={groupItems.map(item => item.id)}
        strategy={verticalListSortingStrategy}
      >
        {groupItems.length === 0 ? (
          externalPanelDropTarget?.groupId === grouping.groups[groupIndex]?.id ? (
            renderExternalDropIndicator(
              `drop-indicator:${grouping.groups[groupIndex]?.id}:empty`
            )
          ) : (
            <div className={groupStyles.emptyGroupPlaceholder}>
              <HugeiconsIcon icon={Appointment01Icon} size={18} />
            </div>
          )
        ) : (
          <>
            {groupItems.map((item, itemIndex) => {
              const isPrimary = !primaryItemAssigned;
              if (isPrimary) {
                primaryItemAssigned = true;
              }
              return (
                <div key={item.id}>
                  {externalPanelDropTarget?.groupId ===
                    grouping.groups[groupIndex]?.id &&
                  externalPanelDropTarget?.beforeItemId === item.id
                    ? renderExternalDropIndicator(
                        `drop-indicator:${grouping.groups[groupIndex]?.id}:${item.id}`
                      )
                    : null}
                  <SortableItem
                    item={item}
                    onItemComplete={handleItemComplete}
                    enableDragHandle={enableDragHandle}
                    itemClassName={itemClassName}
                    isNew={initialNewIds?.has(item.id)}
                    selectable={selection?.selectable}
                    selected={selectedIdsSet.has(item.id)}
                    onSelectedChange={() => handleItemSelectChange(item.id)}
                    dimmed={currentFocusedItems.length > 0}
                    dragHidden={
                      calendarDropActiveId === item.id ||
                      shouldHideSourceItemForPendingFocusCommit(item.id)
                    }
                    dataAttributes={{
                      'data-onboarding-action-item': isPrimary
                        ? 'primary'
                        : 'true',
                      'data-onboarding-action-item-index': String(itemIndex),
                      'data-onboarding-action-group-index': String(groupIndex),
                    }}
                  />
                </div>
              );
            })}
            {externalPanelDropTarget?.groupId === grouping.groups[groupIndex]?.id &&
            !externalPanelDropTarget?.beforeItemId
              ? renderExternalDropIndicator(
                  `drop-indicator:${grouping.groups[groupIndex]?.id}:end`
                )
              : null}
          </>
        )}
      </SortableContext>
    );

    return (
      <div className={className} style={style}>
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          autoScroll={dndAutoScrollEnabled}
          // In grouped mode we must allow crossing group containers.
          // `restrictToParentElement` would clamp the drag within the source group.
          modifiers={[
            restrictToVerticalAxis,
          ]}
        >
          <div style={{ ...containerStyle }}>
            {onSetFocusedItems && (
              <FocusAreaDropZone>
                <FocusDropZone
                  focusedItems={currentFocusedItems}
                  onItemComplete={onItemComplete}
                  dropActive={focusAreaDropActive}
                  dropIndicatorItem={activeItem}
                />
                {headerComponent}
              </FocusAreaDropZone>
            )}

            {!onSetFocusedItems ? headerComponent : null}

            <div>
              {displayGroups.map((group, groupIndex) => {
                const isDocumentGroup =
                  grouping.groupBy === 'pageId' &&
                  group.metadata?.isDocumentGroup;
                const isBacklogGroup =
                  isBacklogGrouping && group.id === NO_DUE_DATE_KEY;
                const titleClassName = isDocumentGroup
                  ? groupStyles.groupDividerTextMeetingWrap
                  : group.title === 'Today'
                    ? groupStyles.groupDividerTextToday
                    : groupStyles.groupDividerText;
                const scheduleStats = groupScheduleStats.get(group.id);
                const showScheduledPercent =
                  scheduleStats !== undefined &&
                  scheduleStats.totalIncomplete > 0;
                const isFullyScheduled =
                  showScheduledPercent &&
                  scheduleStats.scheduledPercent === 100;
                const scheduledPercentLabel = showScheduledPercent
                  ? scheduleStats.scheduledPercent === 0
                    ? '· Unscheduled'
                    : scheduleStats.scheduledPercent === 100
                      ? '· Fully scheduled'
                      : `· ${scheduleStats.scheduledPercent}% scheduled`
                  : null;
                const scheduledPercentTitle = showScheduledPercent
                  ? scheduleStats.scheduledPercent === 0
                    ? 'No action items scheduled'
                    : `${scheduleStats.scheduledPercent}% of action items scheduled`
                  : undefined;
                const scheduledPercentNode = showScheduledPercent ? (
                  <span
                    className={groupStyles.groupDividerScheduledPercent}
                    data-complete={isFullyScheduled || undefined}
                    title={scheduledPercentTitle}
                  >
                    {scheduledPercentLabel}
                  </span>
                ) : null;
                const titleContent = isDocumentGroup ? (
                  <>
                    <span className={groupStyles.groupDividerMeetingName}>
                      {group.title}
                    </span>
                    {group.metadata?.meetingDateLabel ? (
                      <span className={groupStyles.groupDividerMeetingDate}>
                        · {group.metadata.meetingDateLabel}
                      </span>
                    ) : null}
                    {scheduledPercentNode}
                  </>
                ) : (
                  <>
                    <span>
                      {isBacklogGroup
                        ? `Backlog (${group.items.length})`
                        : group.title}
                    </span>
                    {scheduledPercentNode}
                  </>
                );
                const handleTitleClick = () => {
                  if (isBacklogGroup) {
                    setCollapsedGroupIds(prev => {
                      const next = new Set(prev);
                      if (next.has(group.id)) next.delete(group.id);
                      else next.add(group.id);
                      return next;
                    });
                    return;
                  }
                  grouping.onGroupTitleClick?.(group);
                };
                const headerActions =
                  selection && group.items.length > 0 ? (
                    <button
                      type="button"
                      className={clsx(
                        groupStyles.selectAllButton,
                        selection.selectedIds.length > 0 &&
                          groupStyles.selectAllButtonVisible
                      )}
                      onClick={e => {
                        e.stopPropagation();
                        handleSelectAll(group.items);
                      }}
                      title={
                        group.items.every(i => selectedIdsSet.has(i.id))
                          ? 'Deselect all'
                          : 'Select all'
                      }
                    >
                      <HugeiconsIcon icon={CheckListIcon} size={14} />
                    </button>
                  ) : null;
                const isTitleClickable =
                  isBacklogGroup || Boolean(grouping.onGroupTitleClick);
                const titleCursor = isTitleClickable ? 'pointer' : 'default';

                return (
                  <ActionItemGroupComponent
                    key={group.id}
                    title={titleContent}
                    titleClassName={titleClassName}
                    onTitleClick={
                      isTitleClickable ? handleTitleClick : undefined
                    }
                    titleStyle={{ cursor: titleCursor }}
                    headerActions={headerActions}
                    dataAttributes={{
                      'data-onboarding-action-group': 'true',
                      'data-onboarding-action-group-index': String(groupIndex),
                    }}
                    renderList={listClassName => (
                      <GroupDropZone
                        groupId={group.id}
                        className={listClassName}
                      >
                        {isBacklogGroup && collapsedGroupIds.has(group.id) ? (
                          externalPanelDropTarget?.groupId === group.id ? (
                            renderExternalDropIndicator(
                              `drop-indicator:${group.id}:collapsed`
                            )
                          ) : (
                            <div style={{ minHeight: 10 }} />
                          )
                        ) : (
                          renderGroupItems(group.items, groupIndex)
                        )}
                      </GroupDropZone>
                    )}
                  />
                );
              })}
            </div>
          </div>

          <DragOverlay dropAnimation={null}>
            {activeItem ? (
              <div
                className={itemClassName}
                style={{
                  opacity: 1,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                  cursor: 'grabbing',
                  backgroundColor:
                    calendarHoverActiveId === activeItem.id
                      ? 'color-mix(in srgb, var(--affine-background-primary-color) 40%, transparent)'
                      : 'var(--affine-background-primary-color)',
                  borderRadius: '14px',
                }}
              >
                <TodoItemComponent
                  item={activeItem}
                  isDraggable={enableDragHandle}
                  isNew={initialNewIds?.has(activeItem.id)}
                  compact={true}
                  onComplete={() => {}}
                  dragging={calendarHoverActiveId === activeItem.id}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    );
  }

  // Ungrouped mode
  const displayItems = hideFocusedItems
    ? items.filter(item => !hiddenFocusedIds.has(item.id))
    : items;
  let primaryItemAssigned = false;

  return (
    <div className={className} style={style}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        autoScroll={dndAutoScrollEnabled}
        modifiers={[
          restrictToVerticalAxis,
        ]}
      >
        <div style={{ ...containerStyle }}>
          {onSetFocusedItems && (
            <FocusAreaDropZone>
              <FocusDropZone
                focusedItems={currentFocusedItems}
                onItemComplete={onItemComplete}
                dropActive={focusAreaDropActive}
              />
              {headerComponent}
            </FocusAreaDropZone>
          )}
          {!onSetFocusedItems ? headerComponent : null}
          <SortableContext
            items={displayItems.map(item => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                ...containerStyle,
              }}
            >
              {displayItems.map((item, itemIndex) => {
                const isPrimary = !primaryItemAssigned;
                if (isPrimary) {
                  primaryItemAssigned = true;
                }
                return (
                  <div key={item.id}>
                    {externalPanelDropTarget?.beforeItemId === item.id &&
                    !externalPanelDropTarget.groupId
                      ? renderExternalDropIndicator(
                          `drop-indicator:ungrouped:${item.id}`
                        )
                      : null}
                    <SortableItem
                      item={item}
                      onItemComplete={handleItemComplete}
                      enableDragHandle={enableDragHandle}
                      itemClassName={itemClassName}
                      isNew={initialNewIds?.has(item.id)}
                      selectable={selection?.selectable}
                      selected={selectedIdsSet.has(item.id)}
                      onSelectedChange={() => handleItemSelectChange(item.id)}
                      dimmed={currentFocusedItems.length > 0}
                      dragHidden={
                        calendarDropActiveId === item.id ||
                        shouldHideSourceItemForPendingFocusCommit(item.id)
                      }
                      dataAttributes={{
                        'data-onboarding-action-item': isPrimary
                          ? 'primary'
                          : 'true',
                        'data-onboarding-action-item-index': String(itemIndex),
                        'data-onboarding-action-group-index': '0',
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </SortableContext>
        </div>
        <DragOverlay dropAnimation={null}>
          {activeItem ? (
            <div
              className={itemClassName}
              style={{
                opacity: 1,
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                cursor: 'grabbing',
                backgroundColor:
                  calendarHoverActiveId === activeItem.id
                    ? 'color-mix(in srgb, var(--affine-background-primary-color) 40%, transparent)'
                    : 'var(--affine-background-primary-color)',
                borderRadius: '14px',
              }}
            >
              <TodoItemComponent
                item={activeItem}
                isDraggable={enableDragHandle}
                isNew={initialNewIds?.has(activeItem.id)}
                compact={true}
                onComplete={() => {}}
                dragging={calendarHoverActiveId === activeItem.id}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
